import React, { useEffect, useState, useRef } from 'react';
import { Sparkles, Send, MapPin, Clock, Navigation, Settings, GripVertical, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';

function DraggableStop({ inv, index, onDragStart, onDragOver, onDrop, color }) {
  return (
    <div
      draggable
      onDragStart={() => onDragStart(index)}
      onDragOver={(e) => { e.preventDefault(); onDragOver(index); }}
      onDrop={() => onDrop(index)}
      style={{
        display: 'flex', alignItems: 'center', gap: 14, padding: '12px 20px',
        borderTop: index ? '1px solid #f8fafc' : 'none',
        background: '#fff', cursor: 'grab', transition: 'background 0.1s'
      }}>
      <GripVertical size={16} color="#cbd5e1" style={{ flexShrink: 0, cursor: 'grab' }} />
      <div style={{
        width: 28, height: 28, borderRadius: '50%', background: color,
        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 12, fontWeight: 700, flexShrink: 0
      }}>
        {index + 1}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, color: '#1e293b', fontSize: 13 }}>{inv.customer_name}</div>
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
          {inv.customer_code} · {inv.salesman_name}
          {inv.notes && ` · ${inv.notes}`}
        </div>
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
          📍 {parseFloat(inv.latitude).toFixed(4)}, {parseFloat(inv.longitude).toFixed(4)}
        </div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
          AED {Number(inv.invoice_value || 0).toLocaleString()}
        </div>
      </div>
    </div>
  );
}

export default function RoutePlanner() {
  const [sessions, setSessions] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [optimized, setOptimized] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [depot, setDepot] = useState({ name: 'Main Warehouse', lat: '24.97599280117514', lon: '55.18671485766975' });
  const [showDepot, setShowDepot] = useState(false);
  const [depotForm, setDepotForm] = useState(depot);
  const [stats, setStats] = useState(null);
  const dragIndex = useRef(null);

  const findDuplicates = (sessionList) => {
    const grouped = {};
    sessionList.forEach(s => {
      const key = s.driver_id + '_' + s.session_date;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(s);
    });
    const dups = Object.values(grouped).filter(g => g.length > 1);
    setDuplicates(dups);
  };

  const mergeSessions = async (keepId, mergeId, driverName) => {
    if (!window.confirm(`Merge both sessions for ${driverName} into one combined route?`)) return;
    try {
      const r = await api.post('/invoices/merge-sessions', { keep_session_id: keepId, merge_session_id: mergeId });
      toast.success(`Merged! Combined session now has ${r.data.total} stops`);
      const fresh = await api.get('/invoices/sessions');
      const pending = fresh.data.filter(s => ['pending', 'route_planned'].includes(s.status) || (s.status === 'in_progress' && Number(s.new_stops) > 0));
      setSessions(pending);
      findDuplicates(pending);
      setSelected(null); setInvoices([]); setOptimized(false); setStats(null);
    } catch { toast.error('Error merging sessions'); }
  };

  useEffect(() => {
    api.get('/invoices/sessions').then(r => {
      const pending = r.data.filter(s => ['pending', 'route_planned'].includes(s.status) || (s.status === 'in_progress' && Number(s.new_stops) > 0));
      setSessions(pending);
      findDuplicates(pending);
    });
    api.get('/settings').then(r => {
      const s = r.data;
      const d = {
        name: s.depot_name || 'Main Warehouse',
        lat: s.depot_lat || '24.97599280117514',
        lon: s.depot_lon || '55.18671485766975'
      };
      setDepot(d);
      setDepotForm(d);
    }).catch(() => {});
  }, []);

  const selectSession = async (s) => {
    setSelected(s); setOptimized(false); setStats(null);
    const r = await api.get('/invoices/session/' + s.id);
    setInvoices(r.data.sort((a, b) => (a.stop_order || 999) - (b.stop_order || 999)));
  };

  const reoptimize = async () => {
    if (!selected) return;
    if (!window.confirm('Re-optimize the driver\'s REMAINING stops from their current location?\n\nTell the driver to check their app — their stop order will change.')) return;
    try {
      const r = await api.post('/api/routes/reoptimize/' + selected.id, {});
      toast.success(`Re-optimized ${r.data.reoptimized} remaining stops (from ${r.data.started_from === 'driver_gps' ? 'driver location' : 'depot'})`);
      const inv = await api.get('/invoices/session/' + selected.id);
      setInvoices(inv.data);
    } catch (e) { toast.error(e.response?.data?.error || 'Re-optimize failed'); }
  };
  const optimize = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const r = await api.post('/api/routes/optimize/' + selected.id, {
        depot_lat: parseFloat(depot.lat),
        depot_lon: parseFloat(depot.lon)
      });
      setStats(r.data);
      const inv = await api.get('/invoices/session/' + selected.id);
      setInvoices(inv.data.sort((a, b) => (a.stop_order || 999) - (b.stop_order || 999)));
      setOptimized(true);
      toast.success(`✨ Route optimized! ${r.data.total_distance} km · ~${r.data.estimated_duration} mins`);
    } catch { toast.error('Optimization failed'); }
    finally { setLoading(false); }
  };

  const saveManualOrder = async () => {
    setSaving(true);
    try {
      await Promise.all(invoices.map((inv, i) =>
        api.post('/api/routes/update-stop-order', { invoice_id: inv.id, stop_order: i + 1 })
      ));
      toast.success('Manual order saved!');
      setOptimized(true);
    } catch { toast.error('Error saving order'); }
    finally { setSaving(false); }
  };

  const assign = async () => {
    try {
      await api.post('/api/routes/assign/' + selected.id);
      toast.success('Route assigned to driver! 🚀');
      setSessions(s => s.filter(x => x.id !== selected.id));
      setSelected(null); setInvoices([]); setOptimized(false); setStats(null);
    } catch { toast.error('Error assigning route'); }
  };

  const saveDepot = async () => {
    try {
      await api.post('/settings/bulk', {
        settings: { depot_name: depotForm.name, depot_lat: depotForm.lat, depot_lon: depotForm.lon }
      });
      setDepot(depotForm);
      setShowDepot(false);
      toast.success('Depot location saved!');
    } catch { toast.error('Error saving depot'); }
  };

  // Drag & drop handlers
  const onDragStart = (index) => { dragIndex.current = index; };
  const onDragOver = (index) => {
    if (dragIndex.current === null || dragIndex.current === index) return;
    const reordered = [...invoices];
    const dragged = reordered.splice(dragIndex.current, 1)[0];
    reordered.splice(index, 0, dragged);
    dragIndex.current = index;
    setInvoices(reordered);
    setOptimized(false);
  };
  const onDrop = () => { dragIndex.current = null; };

  const COLORS = ['#0284c7', '#16a34a', '#dc2626', '#7c3aed', '#ea580c'];
  const sessionIndex = sessions.findIndex(s => s.id === selected?.id);
  const color = COLORS[sessionIndex % COLORS.length] || '#0284c7';

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', margin: 0 }}>Route Planner</h1>
          <p style={{ color: '#64748b', fontSize: 13, margin: '4px 0 0' }}>AI-optimized delivery routes · Drag to reorder manually</p>
        </div>
        <button onClick={() => setShowDepot(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, cursor: 'pointer', color: '#475569' }}>
          <Settings size={15} /> Depot: {depot.name}
        </button>
      </div>

      {/* Depot settings modal */}
      {showDepot && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: '#1e293b', margin: '0 0 20px' }}>🏭 Depot / Warehouse Location</h2>
            {[['Depot name', 'name', 'text', 'Main Warehouse'],
              ['Latitude', 'lat', 'text', '24.97599'],
              ['Longitude', 'lon', 'text', '55.18671']].map(([label, key, type, ph]) => (
              <div key={key} style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 5 }}>{label}</label>
                <input type={type} value={depotForm[key]} onChange={e => setDepotForm({ ...depotForm, [key]: e.target.value })}
                  placeholder={ph}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
              </div>
            ))}
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 16 }}>
              💡 Tip: Get coordinates from Google Maps — right-click on your warehouse location
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => { setShowDepot(false); setDepotForm(depot); }}
                style={{ flex: 1, padding: 10, background: '#f1f5f9', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={saveDepot}
                style={{ flex: 1, padding: 10, background: '#0284c7', border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Save depot</button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate session warning */}
      {duplicates.map((group, gi) => (
        <div key={gi} style={{ background: '#fef3c7', borderRadius: 10, padding: '12px 16px', marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid #fbbf24' }}>
          <div>
            <div style={{ fontWeight: 600, color: '#92400e', fontSize: 13 }}>
              ⚠️ {group[0].driver_name} has {group.length} separate sessions on {new Date(group[0].session_date).toLocaleDateString()}
            </div>
            <div style={{ fontSize: 12, color: '#b45309', marginTop: 2 }}>
              Session 1: {group[0].total_invoices} stops · Session 2: {group[1].total_invoices} stops — merge into one route?
            </div>
          </div>
          <button onClick={() => mergeSessions(group[0].id, group[1].id, group[0].driver_name)}
            style={{ padding: '8px 14px', background: '#d97706', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0, marginLeft: 12 }}>
            🔀 Merge Now
          </button>
        </div>
      ))}

      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 20 }}>
        {/* Session list */}
        <div style={{ background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', height: 'fit-content' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Pending sessions ({sessions.length})
          </div>
          {sessions.length === 0 && (
            <div style={{ color: '#94a3b8', fontSize: 12, textAlign: 'center', padding: 20 }}>No pending sessions</div>
          )}
          {sessions.map((s, i) => (
            <div key={s.id} onClick={() => selectSession(s)}
              style={{
                padding: '12px', borderRadius: 8, marginBottom: 8, cursor: 'pointer',
                background: selected?.id === s.id ? '#eff6ff' : '#f8fafc',
                border: selected?.id === s.id ? '1.5px solid #bfdbfe' : '1.5px solid transparent',
                borderLeft: `4px solid ${COLORS[i % COLORS.length]}`
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontWeight: 600, color: '#1e293b', fontSize: 13 }}>{s.driver_name}</span>
                {s.status === 'in_progress' && Number(s.new_stops) > 0 && (
                  <span style={{ fontSize: 10, fontWeight: 700, background: '#fef3c7', color: '#b45309', padding: '1px 7px', borderRadius: 10 }}>
                    +{s.new_stops} new · running
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>
                {new Date(s.session_date).toLocaleDateString()} · {s.total_invoices} stops
              </div>
              <div style={{ fontSize: 12, color: '#0284c7', marginTop: 4, fontWeight: 500 }}>
                AED {Number(s.total_value || 0).toLocaleString()}
              </div>
            </div>
          ))}
        </div>

        {/* Route detail */}
        <div>
          {selected ? (
            <>
              <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                  <div>
                    <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 16 }}>{selected.driver_name}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                      {selected.total_invoices} stops · AED {Number(selected.total_value || 0).toLocaleString()} · From: {depot.name}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {selected.status === 'in_progress' ? (
                      <button onClick={reoptimize}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px', background: '#4f46e5', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                        <Sparkles size={14} /> Re-optimize remaining
                      </button>
                    ) : (
                      <button onClick={optimize} disabled={loading}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px', background: loading ? '#e0e7ff' : '#7c3aed', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer' }}>
                        <Sparkles size={14} /> {loading ? 'Optimizing...' : 'AI Optimize'}
                      </button>
                    )}
                    <button onClick={saveManualOrder} disabled={saving}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px', background: '#f59e0b', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                      <Save size={14} /> {saving ? 'Saving...' : 'Save Order'}
                    </button>
                    {optimized && (
                      <button onClick={assign}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px', background: '#16a34a', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                        <Send size={14} /> Assign to Driver
                      </button>
                    )}
                  </div>
                </div>

                {stats && (
                  <div style={{ display: 'flex', gap: 12, padding: '10px 14px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0', marginTop: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#16a34a', fontWeight: 500 }}>
                      <Navigation size={14} /> {stats.total_distance} km total distance
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#16a34a', fontWeight: 500 }}>
                      <Clock size={14} /> ~{stats.estimated_duration} mins estimated
                    </div>
                  </div>
                )}

                {!optimized && invoices.length > 0 && (
                  <div style={{ marginTop: 12, padding: '8px 12px', background: '#fef9c3', borderRadius: 8, fontSize: 12, color: '#854d0e' }}>
                    💡 Click <strong>AI Optimize</strong> to auto-sort stops, or drag rows manually then click <strong>Save Order</strong>
                  </div>
                )}
              </div>

              <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                <div style={{ padding: '12px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>
                    {optimized ? '✅ Optimized stop order' : '⟺ Drag rows to reorder manually'}
                  </span>
                  <span style={{ fontSize: 12, color: '#94a3b8' }}>{invoices.length} stops</span>
                </div>

                {/* Depot start */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 20px', background: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#475569', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>🏭</div>
                  <div>
                    <div style={{ fontWeight: 600, color: '#475569', fontSize: 13 }}>{depot.name} (Start)</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{parseFloat(depot.lat).toFixed(5)}, {parseFloat(depot.lon).toFixed(5)}</div>
                  </div>
                </div>

                {invoices.map((inv, i) => (
                  <DraggableStop key={inv.id} inv={inv} index={i} color={color}
                    onDragStart={onDragStart} onDragOver={onDragOver} onDrop={onDrop} />
                ))}

                {/* Return to depot */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 20px', background: '#f8fafc', borderTop: '1px solid #f1f5f9' }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#475569', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>🏁</div>
                  <div style={{ fontWeight: 600, color: '#475569', fontSize: 13 }}>{depot.name} (Return)</div>
                </div>
              </div>
            </>
          ) : (
            <div style={{ background: '#fff', borderRadius: 12, padding: 60, textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <MapPin size={40} color="#cbd5e1" style={{ marginBottom: 12 }} />
              <div style={{ color: '#94a3b8', fontSize: 14, marginBottom: 6 }}>Select a driver session on the left</div>
              <div style={{ color: '#cbd5e1', fontSize: 12 }}>AI optimize or drag to reorder manually, then assign to driver</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
